import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import { useIntl } from 'react-intl';

const UPDATE_USER_MUTATION = gql`
  mutation UpdateUser(
    $defined_investment_style: Boolean
    $defined_service_model: Boolean
    $part_of_team: Boolean
    $team_size: Int
    $number_of_employees: Int
    $niche_market: Boolean
    $good_standing_with_regulator: Boolean
    $persistency: Int
  ) {
    updateCurrentUser(
      defined_investment_style: $defined_investment_style
      defined_service_model: $defined_service_model
      part_of_team: $part_of_team
      team_size: $team_size
      number_of_employees: $number_of_employees
      niche_market: $niche_market
      good_standing_with_regulator: $good_standing_with_regulator
      persistency: $persistency
    )
  }
`;

const AdditionalPracticeInformationEditModal = ({ user, onClose, onSave, onError }) => {
  const intl = useIntl();
  const [definedInvestmentStyle, setDefinedInvestmentStyle] = useState(user.defined_investment_style || false);
  const [definedServiceModel, setDefinedServiceModel] = useState(user.defined_service_model || false);
  const [partOfTeam, setPartOfTeam] = useState(user.part_of_team || false);
  const [teamSize, setTeamSize] = useState(user.team_size || 1);
  const [numberOfEmployees, setNumberOfEmployees] = useState(user.number_of_employees || 0);
  const [nicheMarket, setNicheMarket] = useState(user.niche_market || false);
  const [goodStandingWithRegulator, setGoodStandingWithRegulator] = useState(user.good_standing_with_regulator || false);
  const [persistency, setPersistency] = useState(user.persistency || 100);

  const [updateUser, { loading }] = useMutation(UPDATE_USER_MUTATION, {
    onCompleted: () => {
      onSave('Additional practice information updated');
      onClose();
    },
    onError: (error) => {
      console.error('Error updating additional practice information:', error);
      onError(error.message);
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await updateUser({
        variables: {
          defined_investment_style: definedInvestmentStyle,
          defined_service_model: definedServiceModel,
          part_of_team: partOfTeam,
          team_size: parseInt(teamSize, 10),
          number_of_employees: parseInt(numberOfEmployees, 10),
          niche_market: nicheMarket,
          good_standing_with_regulator: goodStandingWithRegulator,
          persistency: parseInt(persistency, 10),
        },
      });
    } catch (error) {
      console.error('Error in handleSubmit:', error);
    }
  };

  const BooleanField = ({ label, value, onChange, helpText }) => (
    <div>
      <label className="block text-sm font-medium text-succession-text-primary mb-3">
        {label}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => onChange(false)}
          className={`px-4 py-3 rounded-lg border transition-all text-center ${
            value === false
              ? 'border-succession-primary bg-succession-primary text-white'
              : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium flex-1">{intl.formatMessage({ id: 'succession.profile_edit.common.false' })}</span>
            {value === false && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </button>
        <button
          type="button"
          onClick={() => onChange(true)}
          className={`px-4 py-3 rounded-lg border transition-all text-center ${
            value === true
              ? 'border-succession-primary bg-succession-primary text-white'
              : 'border-succession-border bg-succession-bg-primary text-succession-text-primary hover:border-succession-primary'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="font-medium flex-1">{intl.formatMessage({ id: 'succession.profile_edit.common.true' })}</span>
            {value === true && (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </button>
      </div>
      {helpText && (
        <p className="mt-2 text-xs text-succession-text-muted">{helpText}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4" onClick={onClose}>
      <div className="bg-succession-bg-secondary rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 bg-succession-bg-secondary border-b border-succession-border p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-succession-text-primary">{intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.title' })}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 hover:bg-succession-bg-primary rounded-full transition-colors"
            >
              <svg className="w-6 h-6 text-succession-text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 180px)' }}>
            <div className="p-6 space-y-6">
              <BooleanField
                label={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.defined_investment_style_label' })}
                value={definedInvestmentStyle}
                onChange={setDefinedInvestmentStyle}
                helpText={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.defined_investment_style_help' })}
              />

              <BooleanField
                label={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.defined_service_model_label' })}
                value={definedServiceModel}
                onChange={setDefinedServiceModel}
                helpText={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.defined_service_model_help' })}
              />

              <BooleanField
                label={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.part_of_team_label' })}
                value={partOfTeam}
                onChange={setPartOfTeam}
                helpText={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.part_of_team_help' })}
              />

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.team_size_label' })}
                </label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.team_size_placeholder' })}
                  className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                />
                <p className="mt-1 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.team_size_hint' })}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.number_of_employees_label' })}
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={numberOfEmployees}
                  onChange={(e) => setNumberOfEmployees(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.number_of_employees_placeholder' })}
                  className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                />
                <p className="mt-1 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.number_of_employees_hint' })}
                </p>
              </div>

              <BooleanField
                label={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.niche_market_label' })}
                value={nicheMarket}
                onChange={setNicheMarket}
                helpText={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.niche_market_help' })}
              />

              <BooleanField
                label={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.good_standing_label' })}
                value={goodStandingWithRegulator}
                onChange={setGoodStandingWithRegulator}
                helpText={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.good_standing_help' })}
              />

              <div>
                <label className="block text-sm font-medium text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.persistency_label' })}
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={persistency}
                  onChange={(e) => setPersistency(e.target.value)}
                  placeholder={intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.persistency_placeholder' })}
                  className="w-full px-4 py-2 border border-succession-border rounded-lg bg-succession-bg-primary text-succession-text-primary placeholder-succession-text-muted focus:outline-none focus:ring-2 focus:ring-succession-primary"
                />
                <p className="mt-1 text-xs text-succession-text-muted">
                  {intl.formatMessage({ id: 'succession.profile_edit.modals.additional_practice_information_edit.persistency_hint' })}
                </p>
              </div>
            </div>
          </div>

          {/* Fixed Footer */}
          <div className="flex-shrink-0 flex justify-end gap-3 p-6 border-t border-succession-border bg-succession-bg-secondary">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-primary transition-colors"
              disabled={loading}
            >
              {intl.formatMessage({ id: 'succession.profile_edit.common.cancel' })}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-succession-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? intl.formatMessage({ id: 'succession.profile_edit.common.saving' }) : intl.formatMessage({ id: 'succession.profile_edit.common.save_changes' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AdditionalPracticeInformationEditModal.propTypes = {
  user: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

export default AdditionalPracticeInformationEditModal;
